import React from 'react';

import type { AgentInfo } from '../../lib/agents';
import { AGENTS } from '../../lib/agents';
import { cn } from '../../lib/utils';
import { Button } from '../ui';

interface SessionSidebarProps {
  isGuest: boolean;
  currentAgent: AgentInfo;
  journeyName?: string;
  desiredOutcome?: string;
  insights: { id: string; text: string }[];
  onSwitchAgent?: (agentId: string) => void;
  onSignUp?: () => void;
  className?: string;
}

/**
 * Right sidebar for the session page.
 * - Authenticated users see journey info, saved insights, and agent switcher.
 * - Guests see a subtle sign-up prompt.
 */
export function SessionSidebar({
  isGuest,
  currentAgent,
  journeyName,
  desiredOutcome,
  insights,
  onSwitchAgent,
  onSignUp,
  className,
}: SessionSidebarProps): React.JSX.Element {
  return (
    <aside
      className={cn(
        'flex flex-col gap-6 border-l border-border bg-surface-raised p-5 overflow-y-auto',
        className,
      )}
      aria-label="Session sidebar"
    >
      {isGuest ? (
        /* Guest sidebar */
        <div className="flex flex-col items-center gap-4 text-center py-8">
          <div className="rounded-full bg-surface-muted p-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-8 w-8 text-text-muted"
            >
              <path
                fillRule="evenodd"
                d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-sm text-text-secondary leading-relaxed">
            Join Us to save this conversation and track your progress
          </p>
          <Button variant="default" size="sm" onClick={onSignUp} className="w-full">
            Join Us
          </Button>
        </div>
      ) : (
        <>
          {/* Journey info */}
          {journeyName && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
                Current Journey
              </h3>
              <p className="text-sm font-medium text-text-primary">{journeyName}</p>
              {desiredOutcome && (
                <p className="mt-1 text-xs text-text-secondary line-clamp-3">
                  {desiredOutcome}
                </p>
              )}
            </section>
          )}

          {/* Insights saved from this session */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
              Insights ({insights.length})
            </h3>
            {insights.length === 0 ? (
              <p className="text-xs text-text-muted italic">
                No insights saved yet. Highlight text to save one.
              </p>
            ) : (
              <ul className="space-y-2">
                {insights.map((insight) => (
                  <li
                    key={insight.id}
                    className="rounded border border-border bg-surface p-2 text-xs text-text-secondary line-clamp-3"
                  >
                    {insight.text}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Switch Agent */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
              Switch Agent
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {AGENTS.map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => onSwitchAgent?.(agent.id)}
                  disabled={agent.id === currentAgent.id}
                  title={`${agent.name} — ${agent.coreMessage}`}
                  className={cn(
                    'h-7 w-7 rounded-full border-2 transition-transform hover:scale-110',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                    agent.id === currentAgent.id
                      ? 'border-text-primary scale-110 cursor-default'
                      : 'border-transparent opacity-70 hover:opacity-100',
                  )}
                  style={{ backgroundColor: agent.colour }}
                  aria-label={`Switch to ${agent.name}`}
                />
              ))}
            </div>
          </section>
        </>
      )}
    </aside>
  );
}
